import { AppEnvironmentEnum, AppIdEnum, deleteProperty, getValueByPath, setProperty } from '@lib/shared';
import { AsyncValidatorFn, ValidatorFn, Validators } from '@angular/forms';
import { LorenzControlTypeEnum, LorenzFormControl, LorenzFormControlOption, LorenzFormElement, LorenzFormGroup, LorenzFormLayoutEnum, LorenzFormList, LorenzFormPayload, PermissionEnum } from '@lib/forms';
import { BehaviorSubject } from 'rxjs';

type Constructor<T = {}> = new (...args: any[]) => T;

// @ts-ignore
export function LorenzFormMixin<T extends Constructor>(Base: T) {
	return class extends Base {
		public _payload: LorenzFormPayload = {};

		payloadChanges = new BehaviorSubject<LorenzFormPayload>({});

		get payload(): LorenzFormPayload {
			return this._payload;
		}

		set payload(value: LorenzFormPayload) {
			if (value !== this._payload) {
				this._payload = value;
				this.payloadChanges.next(value);
			}
		}

		setPayloadProperty<K extends keyof LorenzFormPayload>(property: K, value: any) {
			if (this._payload[property] !== value) {
				this.payload = {
					...this._payload,
					[property]: value
				};
			}
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// Enabled & Disabled (affects also the visibility!)

		public _enabled = true;

		enable() {
			this._enabled = true;
			this.enableValidation();
			if (!this.getDisableValueBuffer()) {
				this.restoreValue();
			}
		}

		isEnabled() {
			return this._enabled;
		}

		disable() {
			this._enabled = false;
			this.disableValidation();
			if (!this.getDisableValueBuffer()) {
				this.backupAndRemoveValue();
			}
		}

		isDisabled() {
			return !this._enabled;
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// THE VALIDATION COMPLEX.

		public _validationsBuffer: {
			sync: ValidatorFn[] | null;
			async: AsyncValidatorFn[] | null;
		} | null = null;

		public _validationEnabled = true;

		isValidationEnabled(): boolean {
			return this._validationEnabled;
		}

		isValidationDisabled(): boolean {
			return !this._validationEnabled;
		}

		setValidation(enable: boolean) {
			if (enable) {
				this.enableValidation();
			} else {
				this.disableValidation();
			}
			(this as any).updateValueAndValidity();
		}

		enableValidation() {
			if (!this.isLorenzFormElement()) {
				return;
			}

			if (this._validationsBuffer === null) {
				this._validationsBuffer = {
					sync: null,
					async: null
				};
			} else {
				// If validation is not activated, fetch the validators from the buffer.
				if (!this._validationEnabled) {
					if (this._validationsBuffer.sync) {
						this.setValidators(this._validationsBuffer.sync);
					}
					if (this._validationsBuffer.async) {
						this.setAsyncValidators(this._validationsBuffer.async);
					}
					this.updateValueAndValidity({ emitEvent: false });

					// Purge the buffer.
					this._validationsBuffer.sync = null;
					this._validationsBuffer.async = null;
				}
			}

			this._validationEnabled = true;

			this._enabled = true;

			if (this instanceof LorenzFormGroup) {
				Object.values(this.controls).forEach((item) => {
					(item as LorenzFormElement).enableValidation();
				});
			} else if (this instanceof LorenzFormList) {
				// If a list is to be activated, it must have at least one item!
				if (this.controls.length === 0 && this.payload.listItemForm instanceof Function) {
					this.addItem();
				}

				this.controls.forEach((item) => {
					(item as LorenzFormGroup).enableValidation();
				});
			}
		}

		disableValidation() {
			if (this.isLorenzFormElement()) {
				// If validation is activated, write the validators to the buffer.
				if (this._validationEnabled) {
					this._validationsBuffer = {
						sync: this.validator ? [this.validator] : null,
						async: this.asyncValidator ? [this.asyncValidator] : null
					};

					// Purge the validators.
					this.clearValidators();
					this.clearAsyncValidators();

					this.updateValueAndValidity({ emitEvent: false });
				}

				this._validationEnabled = false;

				if (this instanceof LorenzFormGroup) {
					Object.values(this.controls).forEach((item) => {
						(item as LorenzFormElement).disableValidation();
					});
				} else if (this instanceof LorenzFormList) {
					this.controls.forEach((item) => {
						(item as LorenzFormElement).disableValidation();
					});
				}
			}
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// THE VALUE COMPLEX.
		public _valueBufferInitialized = false;
		public _valueBuffer = null;

		backupAndRemoveValue(init = false) {
			// if (this.getFullKey() === 'beschaeftigungen_laufend.liste') {
			// 	console.log('BUMM');
			// }

			if (this.forcedValuePatching) {
				return;
			}

			if (this.isLorenzFormElement() && (this._valueBufferInitialized || init)) {
				this._valueBuffer = this.getRawValue();
				this._valueBufferInitialized = true;

				if (this instanceof LorenzFormList) {
					this.clear();
				} else {
					this.patchValue(null, { emitEvent: false });
				}

				// console.log('backupAndRemoveValue', this.getFullKey(), this.value, this._valueBuffer);
			}
		}

		restoreValue() {
			// FOR DEBUGGING
			// if (this.getFullKey() === 'personal.positionen.0.buchungszeit_hinweis') {
			// 	console.log('restoreValue', this.getFullKey(), this._valueBuffer);
			// }

			if (this.forcedValuePatching) {
				return;
			}

			if (this.isLorenzFormElement() && this._valueBufferInitialized) {
				this.patchValue(this._valueBuffer, { emitEvent: true });
				this._valueBuffer = null;

				// console.log('restoreValue', this.getFullKey(), this.value, this._valueBuffer);
			}
		}

		getValueBuffer() {
			return this._valueBuffer;
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// Control is required or not.

		setRequired(required: boolean) {
			if (!this.isLorenzFormElement()) {
				return;
			}

			if (this.isValidationEnabled()) {
				if (required) {
					this.addValidators(Validators.required);
				} else {
					this.removeValidators(Validators.required);
				}
				this.updateValueAndValidity();
			} else {
				if (this._validationsBuffer?.sync) {
					if (required) {
						if (!this._validationsBuffer.sync.includes(Validators.required)) {
							this._validationsBuffer.sync.push(Validators.required);
						}
					} else {
						const idx = this._validationsBuffer.sync.indexOf(Validators.required);
						if (idx !== -1) {
							this._validationsBuffer.sync.splice(idx, 1);
						}
					}
				}
			}
		}

		isRequired(): boolean {
			if (this.payload?.forceOptional) {
				return false;
			}
			return this.isValidationEnabled() && !!(this as any).validator?.({} as any)?.['required'];
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// Control is in form-value or not.
		// inValue(this: any): boolean {
		// 	return this.getPermission() !== PermissionEnum.NONE;
		// }

		// -------------------------------------------------------------------------------------------------------------------- //

		// Control is visible or not in UI.

		isVisible(this: any) {
			if (this.isEnabled() && this.getControlType() !== LorenzControlTypeEnum.HIDDEN) {
				const permissions = this.getPermission();
				return [PermissionEnum.READ, PermissionEnum.WRITEONCE, PermissionEnum.FULL].includes(permissions);
			}
			return false;
		}

		isInvisible() {
			return !this.isVisible();
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// Control is read-only or not.

		isReadonly(this: any) {
			if (this.isEnabled()) {
				let permission = this.getPermission();

				if (permission === PermissionEnum.WRITEONCE) {
					permission = this.payload?.originalValue ? PermissionEnum.READ : PermissionEnum.FULL;
				}

				return [PermissionEnum.NONE, PermissionEnum.HIDDEN, PermissionEnum.READ].includes(permission);
			}

			return true;
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// ForceValuePatching deactivates validators and the other Functions (valueChange etc.) temporarily for the patching.

		// IMPORTANT: ForceValuePatching is a global setting for the root control and all its children!

		public _forcedValuePatching: boolean | null = null;

		// If a control element does not have its own state for this, the first parent with this state decides.
		get forcedValuePatching(): boolean {
			const lorenzFormRoot = this.getRoot();
			return lorenzFormRoot._forcedValuePatching ?? false;
		}

		set forcedValuePatching(value: boolean) {
			const lorenzFormRoot = this.getRoot();
			if (lorenzFormRoot) {
				this._forcedValuePatching = value;
			}
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// The locking overrides the current editability.

		public _locked: boolean | null = null;

		// If a control element does not have its own state for this, the first parent with this state decides.
		isLocked(): boolean {
			let currentControl = this as any;
			while (currentControl) {
				if (currentControl._locked) {
					return true;
				}
				currentControl = currentControl.parent || null;
			}
			return false;
		}

		isUnlocked(): boolean {
			return !this.isLocked();
		}

		setLocked(locked: boolean) {
			this._locked = locked;
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// is...

		isBlocked(): boolean {
			return this.isReadonly() || this.isLocked();
		}

		isExpandable(): boolean {
			return this.payload?.isExpandable ?? false;
		}

		isFaulty(): boolean {
			return ((this as any).invalid && (this as any).touched) ?? false;
		}

		isMissingRequiredValue(this: any): boolean {
			return this.hasRequiredError() && this.isVisible() && !this.isBlocked();
		}

		// TRUE, if the control is the first level in the form hierarchy.
		isFirstLevel(): boolean {
			return (this as any).getPath() === '';
		}

		isHighlighted(this: any): boolean {
			// FOR DEBUGGING
			// if (this.payload.highlightChanges()) {
			// 	console.log('BUMM');
			// }
			if (!this.getHighLightChanges()) {
				return false;
			}

			const lorenzFormRoot = this.getRoot();

			const originalDataRootPath = lorenzFormRoot?.payload?.originalData ?? null;
			if (!originalDataRootPath) {
				return false;
			}

			const originalDataRootControlValue = lorenzFormRoot.getControlValue(originalDataRootPath);
			if (!originalDataRootControlValue) {
				return false;
			}

			const fullKey = this.getFullKey();
			const originalValue = getValueByPath(originalDataRootControlValue, fullKey);
			const currentValue = this.value;

			if ((originalValue === '' || originalValue == null) && (currentValue === '' || currentValue == null)) {
				return false;
			}

			return currentValue !== originalValue;
		}

		isLorenzFormElement(): this is LorenzFormElement {
			return this instanceof LorenzFormControl || this instanceof LorenzFormGroup || this instanceof LorenzFormList;
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// has...

		hasControls(this: any): boolean {
			// prettier-ignore
			return typeof this.controls === 'object' &&
				this.controls !== null &&
				Object.keys(this.controls).length > 0;
		}

		hasAddButton(): boolean {
			let current: any = this;
			let lastDefined: boolean | undefined = undefined;

			while (current) {
				if (current.payload?.hasAddButton !== undefined) {
					lastDefined = current.payload.hasAddButton;
				}
				current = current.parent;
			}

			return lastDefined !== undefined ? lastDefined : false;
		}

		hasDeleteButton(): boolean {
			let current: any = this;
			let lastDefined: boolean | undefined = undefined;

			while (current) {
				if (current.payload?.hasDeleteButton !== undefined) {
					lastDefined = current.payload.hasDeleteButton;
				}
				current = current.parent;
			}

			return lastDefined !== undefined ? lastDefined : false;
		}

		hasErrors(this: any): boolean {
			if (this.errors) {
				const errorKeys = Object.keys(this.errors).filter((key) => !(this.payload?.forceOptional && key === 'required'));
				return errorKeys.length > 0;
			}
			return false;
		}

		hasRequiredError(this: any): boolean {
			if (this.payload?.forceOptional) {
				return false;
			}
			return !!this.errors?.['required'];
		}

		hasLabel(this: any): boolean {
			return !!this.getLabel();
		}

		hasPlaceholder(this: any): boolean {
			return !!this.getPlaceholder();
		}

		hasSaveButton(): boolean {
			let current: any = this;
			let lastDefined: boolean | undefined = undefined;

			while (current && lastDefined === undefined) {
				if (current.payload?.hasSaveButton !== undefined) {
					lastDefined = current.payload.hasSaveButton;
				}
				current = current.parent;
			}

			return lastDefined !== undefined ? lastDefined : false;
		}

		hasText(this: any): boolean {
			return !!this.getText();
		}

		hasTextDefaults(this: any): boolean {
			const textDefaults = this.payload?.textDefaults;

			if (!textDefaults) {
				return false;
			}

			if (Array.isArray(textDefaults)) {
				return textDefaults.length > 0;
			}

			return Object.values(textDefaults).some((values) => Array.isArray(values) && values.length > 0);
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// getter

		getAddButtonText(): string {
			let current: any = this;
			let lastDefined: string | undefined = undefined;

			while (current) {
				if (current.payload?.addButtonText !== undefined) {
					lastDefined = current.payload.addButtonText;
				}
				current = current.parent;
			}

			return lastDefined !== undefined ? lastDefined : 'Neuer Eintrag';
		}

		getAppEnvironment(): AppEnvironmentEnum {
			let currentControl = this as any;
			while (currentControl) {
				if (currentControl.payload?.appEnvironment) {
					return currentControl.payload?.appEnvironment;
				}
				currentControl = currentControl.parent || null;
			}
			return AppEnvironmentEnum.LIVE;
		}

		getAppId(): AppIdEnum {
			let currentControl = this as any;
			while (currentControl) {
				if (currentControl.payload?.appId) {
					return currentControl.payload?.appId;
				}
				currentControl = currentControl?.parent || null;
			}
			return AppIdEnum.UNKNOWN;
		}

		getCompletlyHiddenWhileDisabled(): boolean | null {
			return (this as any)?.payload?.completlyHiddenWhileDisabled ?? null;
		}

		getControls(): LorenzFormElement | null {
			return (this as any)?.controls ?? null;
		}

		getControl(lorenzFormControlName: string): LorenzFormControl | null {
			return (this as any)?.get(lorenzFormControlName) ?? null;
		}

		getControlType(): LorenzControlTypeEnum {
			return this.payload?.type ?? LorenzControlTypeEnum.UNKNOWN;
		}

		getControlValue(lorenzFormControlName: string, defaultValue: any = null): any {
			return (this as any)?.get(lorenzFormControlName)?.value ?? defaultValue;
		}

		setControlValue(lorenzFormControlName: string, value: any, options?: any) {
			const lorenzFormControl = (this as any)?.getControl(lorenzFormControlName) ?? null;
			if (!lorenzFormControl) {
				return;
			}
			lorenzFormControl.patchValue(value, options);
			lorenzFormControl.updateValueAndValidity({ onlySelf: true });
		}

		getData(): any {
			return this.payload?.data ?? null;
		}

		getDefaultOption(): string | null {
			return this.payload?.defaultOption ?? null;
		}

		getDirection(): string {
			return this.payload?.direction ?? 'column';
		}

		getDisableValueBuffer(): boolean {
			return this.payload?.disableValueBuffer ?? false;
		}

		setDisableValueBuffer(isDisabled: boolean) {
			this.setPayloadProperty('disableValueBuffer', isDisabled);
		}

		getErrors(this: any): string {
			if (this.hasErrors() && this.errors) {
				const controlErrorMessages = this.payload?.errorMessages ?? null;
				const filteredValues = Object.entries(this.errors).map(([key, _value]) => this.mapErrorMessage(key, controlErrorMessages));
				return filteredValues.join('; ');
			} else {
				return '';
			}
		}

		//Return the path and the key of control.
		getFullKey(): string {
			// prettier-ignore
			return (this.getPath() !== '' && this.getKey() !== '')
				? `${this.getPath()}.${this.getKey()}`
				: (this.getPath() || this.getKey());
		}

		getHighLightChanges(this: any): boolean {
			return this.payload?.displayOptions?.highlightChanges ?? false;
		}

		getInnerHTML(): string | Function {
			return this.payload?.innerHTML ?? '';
		}

		// Returns the name of the control.
		getKey(this: any): string {
			const parent = (this.parent as LorenzFormGroup) ?? null;
			if (!parent) {
				return '';
			}
			return Object.keys(parent['controls']).find((name) => parent['controls'][name] === this) || '';
		}

		getLabel(): string {
			return this.payload?.label ?? '';
		}

		getLabelAfter(): string {
			return this.payload?.labelAfter ?? '';
		}

		getLabelBefore(): string {
			return this.payload?.labelBefore ?? '';
		}

		getList(this: any): LorenzFormList | null {
			const listCandidate = this.parent ?? null;
			if (listCandidate && listCandidate instanceof LorenzFormList) {
				return listCandidate as LorenzFormList;
			}
			return null;
		}

		getRequiredAsterisk(this: any): string {
			if (this.isRequired() && !(this.isLocked() || this.isReadonly())) {
				const cssClass = this.valid ? 'valid' : 'invalid';
				return ` <span class="required ${cssClass}">*<span>`;
			}
			return '';
		}

		getLabelWithTranslation(): string {
			let label = this.getLabel();
			// if (label !== '' && this._services?.translateService) {
			// 	label = this._services.translateService.instant(label);
			// }
			return label;
		}

		getLayout(): LorenzFormLayoutEnum {
			let currentControl = this as any;
			while (currentControl) {
				if (currentControl.payload?.layout) {
					return currentControl.payload.layout;
				}
				currentControl = currentControl.parent || null;
			}
			return LorenzFormLayoutEnum.DEFAULT;
		}

		getLorenzFormType(this: any): LorenzFormGroup | LorenzFormList | LorenzFormControl | any {
			if (this instanceof LorenzFormGroup) {
				return this as unknown as LorenzFormGroup;
			} else if (this instanceof LorenzFormList) {
				return this as unknown as LorenzFormList;
			} else if (this instanceof LorenzFormControl) {
				return this as unknown as LorenzFormControl;
			}
			return this;
		}

		getLorenzFormTypeString(): 'LorenzFormGroup' | 'LorenzFormList' | 'LorenzFormControl' {
			if (this instanceof LorenzFormGroup) {
				return 'LorenzFormGroup';
			} else if (this instanceof LorenzFormList) {
				return 'LorenzFormList';
			} else {
				return 'LorenzFormControl';
			}
		}

		getOptions(language?: string): LorenzFormControlOption[] {
			if (!this.payload?.options) {
				return [];
			}

			// Options can be monolingual or multilingual!

			if (Array.isArray(this.payload.options)) {
				return this.payload.options;
			} else {
				if (language) {
					return this.payload.options[language] ?? [];
				} else {
					const defaultOption = this.getDefaultOption();
					return defaultOption ? this.payload.options[defaultOption] : [];
				}
			}
		}

		// Names from the complete form path to the control (without the control itself)
		getPath(this: any): string {
			const path: string[] = [];
			let currentControl = this.parent;
			while (currentControl) {
				const key = currentControl.getKey();
				if (key) {
					path.unshift(key);
				}
				currentControl = currentControl.parent;
			}

			if (path.length) {
				const helper = path.join('.');
				return helper;
			}

			return '';
		}

		getPermission(): PermissionEnum {
			let currentControl = this as any;
			const appId = this.getAppId();
			while (currentControl) {
				if (currentControl.payload?.permissions?.[appId]) {
					return currentControl.payload?.permissions?.[appId];
				}
				currentControl = currentControl.parent || null;
			}
			return PermissionEnum.NONE;
		}

		getPlaceholder(): string {
			return this.payload?.placeholder ?? '';
		}

		getPreviousValue(): any {
			return this.payload?.previousValue ?? null;
		}

		// Returns the form root control.
		getRoot(): LorenzFormGroup {
			let currentControl = this.getLorenzFormType();
			while (currentControl.parent) {
				currentControl = (currentControl?.parent as LorenzFormGroup) || null;
			}
			return currentControl as LorenzFormGroup;
		}

		getSaveButtonDisabled(this: any): boolean {
			let currentControl = this;
			while (currentControl) {
				const payload = currentControl.payload ?? null;
				if (typeof payload?.saveButtonFn === 'function') {
					if (payload.isSaveButtonDisabled !== null) {
						return payload.isSaveButtonDisabled;
					}
				}
				currentControl = currentControl.parent as LorenzFormGroup;
			}
			return true;
		}

		getSaveButtonFn(this: any): Function | null {
			let currentControl = this;
			while (currentControl) {
				const payload = currentControl.payload ?? null;
				if (typeof payload?.saveButtonFn === 'function') {
					return payload?.saveButtonFn;
				}
				currentControl = currentControl.parent as LorenzFormGroup;
			}
			return null;
		}

		getStatus(): any {
			const lorenzFormRoot = this.getRoot();

			const statusControl = lorenzFormRoot.get('_lorenz.status');

			if (statusControl && statusControl instanceof LorenzFormControl) {
				return statusControl.value;
			}

			return null;
		}

		getStoreFn(this: any): Function | null {
			let currentControl = this;
			let storeFn = null;
			while (currentControl && !storeFn) {
				storeFn = (currentControl.payload?.storeFn as Function) ?? null;
				currentControl = (currentControl.parent as LorenzFormGroup) || null;
			}
			return storeFn;
		}

		getText(): string {
			return this.payload?.text ?? '';
		}

		getTextDefaults(language?: string): string[] {
			if (!this.payload?.textDefaults) {
				return [];
			}

			// TextDefaults can be monolingual or multilingual!

			if (Array.isArray(this.payload.textDefaults)) {
				return this.payload.textDefaults;
			} else {
				if (language) {
					return this.payload.textDefaults[language] ?? [];
				} else {
					const defaultOption = this.getDefaultOption();
					return defaultOption ? this.payload.textDefaults[defaultOption] : [];
				}
			}
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// setter

		setPermission(appId: AppIdEnum, permission: PermissionEnum | null, recurse = false) {
			if (permission === null) {
				deleteProperty(this.payload, `permissions.${appId}`);
			} else {
				setProperty(this.payload, `permissions.${appId}`, permission);
			}
			if (!recurse) {
				return;
			}
			const controls = this.getControls();
			if (controls) {
				Object.values(controls).forEach((child) => {
					child.setPermission(appId, null, true);
				});
			}
		}

		setSaveButtonDisabled(isDisabled: boolean) {
			this.setPayloadProperty('isSaveButtonDisabled', isDisabled);
		}

		// -------------------------------------------------------------------------------------------------------------------- //

		// THE REST

		// TODO - Besser machen
		mapErrorMessage(key: string, controlErrorMessages: any = null): string {
			if (controlErrorMessages && controlErrorMessages[key]) {
				return controlErrorMessages[key];
			}

			switch (key) {
				case 'dateInPast':
					return 'Das Datum darf nicht in der Vergangenheit liegen!';

				case 'dateTooFarInFuture':
					return 'Das Datum liegt zu weit in der Zukunft (maximal ein Jahr)!';

				case 'email':
					return 'Bitte prüfen Sie die eingegebene E-Mail-Adresse!';

				case 'required':
					return 'Dieser Wert wird benötigt!';

				default:
					return 'Bitte prüfen Sie die Eingabe!';
			}
		}

		showErrors(): boolean {
			return this.isFaulty() && this.hasErrors();
		}
	};
}
